import { useEffect, useRef, useState } from 'react';
import { getMedia } from '@/lib/db';

/**
 * Loads (and caches) decoded ImageBitmaps for the given media ids. Bitmaps
 * already in the cache from a previous render are reused; bitmaps no longer
 * referenced are closed to free GPU/CPU memory.
 */
export function useImageBitmaps(mediaIds: string[]): Map<string, ImageBitmap> {
  const cacheRef = useRef<Map<string, ImageBitmap>>(new Map());
  const [, forceRender] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function sync() {
      const cache = cacheRef.current;
      const wanted = new Set(mediaIds);

      // Close and drop bitmaps no longer needed.
      for (const [id, bitmap] of cache) {
        if (!wanted.has(id)) {
          bitmap.close();
          cache.delete(id);
        }
      }

      const missing = mediaIds.filter((id) => !cache.has(id));
      if (missing.length === 0) return;

      const loaded = await Promise.all(
        missing.map(async (id) => {
          const record = await getMedia(id);
          if (!record) return null;
          const bitmap = await createImageBitmap(record.blob);
          return { id, bitmap };
        })
      );

      if (cancelled) return;
      for (const entry of loaded) {
        if (entry) cache.set(entry.id, entry.bitmap);
      }
      forceRender((n) => n + 1);
    }

    void sync();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaIds.join(',')]);

  return cacheRef.current;
}
